import './online-request-status-changer.scss';

import { notification } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import {
  Controller,
  type SubmitHandler,
  useForm,
  useWatch
} from 'react-hook-form';

import { Button, Modal, Select, TextArea } from '@/components/Shared/ui';
import { type ISelectOption } from '@/components/Shared/ui/select/select';
import { DictTypes } from '@/constants';
import { autoCreditApi } from '@/integrations/app.api/auto-credit.api';
import { handleError } from '@/services/handleError';
import { isDictHasAll } from '@/services/helpers/dict';
import { dispatch, useAppSelector } from '@/store';
import { getRequestById } from '@/store/newOrder.slice';

type TForm = {
  comment: string;
  status: string;
  statusCategory: string;
};

const statusCategoryOptions: ISelectOption[] = [
  {
    value: 'contact',
    label: 'Контакт с клиентом'
  },
  {
    value: 'positive',
    label: 'Позитивные действия'
  },
  {
    value: 'negative',
    label: 'Негативные результаты'
  }
];

const OnlineRequestStatusChanger: React.FC = () => {
  const { dicts } = useAppSelector((state) => state.dict);
  const { orderApp } = useAppSelector((state) => state.newOrder);

  const { control, reset, handleSubmit, setValue } = useForm<TForm>({
    mode: 'all',
    delayError: 200,
    defaultValues: {
      comment: '',
      status: '',
      statusCategory: ''
    }
  });

  const statusCategory = useWatch({ control, name: 'statusCategory' });

  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const statusOptions: ISelectOption[] = useMemo(() => {
    if (!isDictHasAll(dicts, [DictTypes.OnlineRequestFcStatuses])) return [];
    return dicts[DictTypes.OnlineRequestFcStatuses].content
      .filter((item) => item.category === statusCategory)
      .sort((a, b) => a.sort_order - b.sort_order)
      .map((item) => ({
        value: String(item.code),
        label: String(item.name)
      }));
  }, [dicts, statusCategory]);

  const onOk: SubmitHandler<TForm> = async (data) => {
    const requestId = orderApp?.onlineClientRequest?.uuid;

    if (!requestId) return;

    try {
      setIsLoading(true);
      await autoCreditApi.changeOnlineRequestStatus({
        comment: data.comment,
        requestId,
        status: data.status
      });
      await dispatch(getRequestById({ reqId: orderApp.uuid }));
      notification.success({ title: 'Статус успешно изменен' });
      setIsModalOpen(false);
    } catch (error) {
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!isModalOpen) {
      reset();
    }
  }, [isModalOpen]);

  return (
    <>
      <Button
        variant="filled-green"
        size="small"
        onClick={() => setIsModalOpen(true)}
      >
        Изменить статус заявки
      </Button>
      <Modal
        title="Изменение статуса заявки"
        open={isModalOpen}
        closable={!isLoading}
        mask={{ closable: false }}
        onClose={() => setIsModalOpen(false)}
      >
        <form
          className="online-request-status-changer__form"
          onSubmit={(e) => {
            e.stopPropagation();
            handleSubmit(onOk)(e);
          }}
        >
          <Controller
            control={control}
            name="statusCategory"
            rules={{
              required: 'Пожалуйста, выберите тип статуса'
            }}
            render={({ field, fieldState: { error } }) => (
              <Select
                {...field}
                isRequired
                label="Тип статуса"
                selectedValue={statusCategoryOptions.find(
                  (item) => item.value === field.value
                )}
                onChange={(status) => {
                  field.onChange(status.value);
                  setValue('status', '');
                }}
                options={statusCategoryOptions}
                error={error?.message}
                isFrozen={isLoading}
              />
            )}
          />
          <Controller
            control={control}
            name="status"
            rules={{
              required: 'Пожалуйста, выберите статус'
            }}
            render={({ field, fieldState: { error } }) => (
              <Select
                {...field}
                isRequired
                label="Статус"
                selectedValue={statusOptions.find(
                  (item) => item.value === field.value
                )}
                onChange={(status) => field.onChange(status.value)}
                options={statusOptions}
                error={error?.message}
                isFrozen={isLoading}
              />
            )}
          />
          <Controller
            control={control}
            name="comment"
            render={({ field }) => (
              <div className="leads-status-modal__field">
                <TextArea
                  {...field}
                  label={`Комментарий (${field.value.length} / 250)`}
                  rows={4}
                  isFrozen={isLoading}
                  maxLength={250}
                />
              </div>
            )}
          />

          <div className="online-request-status-changer__actions">
            <Button
              onClick={() => setIsModalOpen(false)}
              type="button"
              disabled={isLoading}
            >
              Отменить
            </Button>
            <Button variant="filled-red" type="submit" isLoading={isLoading}>
              Применить
            </Button>
          </div>
        </form>
      </Modal>
    </>
  );
};

export default OnlineRequestStatusChanger;
